import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Table,
  Alert,
  ButtonGroup,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchDocumentos,
  uploadDocumento,
  deleteDocumento,
  toggleEstadoDocumento,
  reemplazarArchivoDocumento,
  editarDocumento,
} from "../../redux/documentosSlice";

const DocumentosAdminPage = () => {
  const dispatch = useDispatch();
  const {
    documentos,
    loading,
    error,
    uploadError: serverUploadError,
  } = useSelector((state) => state.documentos);

  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentDetails, setDocumentDetails] = useState({
    nombre: "",
    descripcion: "",
  });

  const [editando, setEditando] = useState(null); // doc en edición
  const [formEdit, setFormEdit] = useState({ titulo: "", descripcion: "" });

  const abrirEditar = (doc) => {
    setFormEdit({
      titulo: doc.titulo || "",
      descripcion: doc.descripcion || "",
    });
    setEditando(doc);
  };

  const guardarEdicion = async () => {
    if (!formEdit.titulo.trim()) {
      Swal.fire(
        "Falta el título",
        "El documento necesita un título.",
        "warning",
      );
      return;
    }
    try {
      await dispatch(
        editarDocumento({ id: editando.id, ...formEdit }),
      ).unwrap();
      setEditando(null);
      Swal.fire({
        icon: "success",
        title: "Documento actualizado",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire(
        "Error",
        typeof err === "string" ? err : "No se pudo editar.",
        "error",
      );
    }
  };
  const [localUploadError, setLocalUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    nombre: "",
    descripcion: "",
    archivo: "",
  });

  useEffect(() => {
    dispatch(fetchDocumentos(true));
  }, [dispatch]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFile(null);
    setDocumentDetails({ nombre: "", descripcion: "" });
    setLocalUploadError(null);
    setValidationErrors({ nombre: "", descripcion: "", archivo: "" });
  };

  const handleShowModal = () => setShowModal(true);

  const validateNombre = (nombre) => {
    if (nombre.trim() === "") {
      return "El nombre del documento es obligatorio.";
    }
    if (nombre.length < 3) {
      return "El nombre debe tener al menos 3 caracteres.";
    }
    if (nombre.length > 50) {
      return "El nombre no puede tener más de 50 caracteres.";
    }
    return "";
  };

  const validateDescripcion = (descripcion) => {
    if (descripcion.length > 200) {
      return "La descripción no puede tener más de 200 caracteres.";
    }
    return "";
  };

  const validateArchivo = (file) => {
    if (!file) {
      return "El archivo es obligatorio.";
    }

    const allowedExtensions = [
      ".pdf",
      ".docx",
      ".doc",
      ".docm",
      ".dotx",
      ".dot",
      ".odt",
      ".ocx",
      ".xlsx",
      ".xls",
      ".xlsm",
      ".xltx",
      ".xlsb",
      ".ods",
      ".lsx",
    ];

    const fileExt = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(`.${fileExt}`)) {
      return `Extensión no permitida. Use: ${allowedExtensions.join(", ")}`;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return "El archivo no puede ser mayor a 10MB.";
    }

    return "";
  };

  const handleNombreChange = (e) => {
    const nombre = e.target.value;
    setDocumentDetails({ ...documentDetails, nombre });
    setValidationErrors({
      ...validationErrors,
      nombre: validateNombre(nombre),
    });
  };

  const handleDescripcionChange = (e) => {
    const descripcion = e.target.value;
    setDocumentDetails({ ...documentDetails, descripcion });
    setValidationErrors({
      ...validationErrors,
      descripcion: validateDescripcion(descripcion),
    });
  };

  const handleArchivoChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setValidationErrors({
      ...validationErrors,
      archivo: validateArchivo(file),
    });
  };

  const isFormValid = () => {
    return (
      validationErrors.nombre === "" &&
      validationErrors.descripcion === "" &&
      validationErrors.archivo === "" &&
      documentDetails.nombre.trim() !== "" &&
      selectedFile !== null
    );
  };

  const handleUploadDocument = async () => {
    setIsUploading(true);
    setLocalUploadError(null);

    const formData = new FormData();
    formData.append("archivo", selectedFile);
    formData.append("nombre", documentDetails.nombre);
    formData.append("descripcion", documentDetails.descripcion);

    try {
      await dispatch(uploadDocumento(formData)).unwrap();
      dispatch(fetchDocumentos());
      handleCloseModal();
      Swal.fire({
        icon: "success",
        title: "Documento subido",
        text: "El documento se ha subido correctamente.",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (error) {
      let errorMessage =
        "Error al subir el documento. Por favor, inténtalo nuevamente.";

      if (error.errors && Array.isArray(error.errors)) {
        errorMessage = error.errors
          .map((err) => err.msg || err.message)
          .join(", ");
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      setLocalUploadError(errorMessage);
      Swal.fire({
        icon: "error",
        title: "Error al subir",
        text: errorMessage,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esta acción.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteDocumento(id)).unwrap();
        dispatch(fetchDocumentos());
        Swal.fire({
          icon: "success",
          title: "Documento eliminado",
          text: "El documento se ha eliminado correctamente.",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo eliminar el documento.",
        });
      }
    }
  };

  const handleToggleEstado = async (doc) => {
    const desactivando = doc.estado_activo;
    const result = await Swal.fire({
      title: desactivando
        ? "¿Ocultar este documento?"
        : "¿Mostrar este documento?",
      text: desactivando
        ? "Dejará de aparecer para los socios y en el buscador de menciones."
        : "Volverá a estar visible.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: desactivando ? "#d0021b" : "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: desactivando ? "Sí, ocultar" : "Sí, mostrar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      console.log(
        "color:#fd7e14",
        doc.id,
        "| estado actual:",
        doc.estado_activo,
      );
      await dispatch(toggleEstadoDocumento(doc.id)).unwrap();
      await dispatch(fetchDocumentos(true));
      Swal.fire({
        icon: "success",
        title: desactivando ? "Documento oculto" : "Documento visible",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire(
        "Error",
        typeof err === "string" ? err : "No se pudo cambiar el estado.",
        "error",
      );
    }
  };

  const handleReemplazarArchivo = (doc) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx,.xls,.xlsx";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const result = await Swal.fire({
        title: "¿Reemplazar el archivo?",
        html: `Se subirá <strong>${file.name}</strong> en lugar del archivo actual de «${doc.titulo}». El título y los enlaces se mantienen.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d0021b",
        confirmButtonText: "Sí, reemplazar",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;
      try {
        Swal.fire({
          title: "Subiendo…",
          didOpen: () => Swal.showLoading(),
          allowOutsideClick: false,
        });
        await dispatch(
          reemplazarArchivoDocumento({ id: doc.id, file }),
        ).unwrap();
        Swal.fire({
          icon: "success",
          title: "Archivo reemplazado",
          timer: 1600,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire(
          "Error",
          typeof err === "string" ? err : "No se pudo reemplazar.",
          "error",
        );
      }
    };
    input.click();
  };

  const getDocumentType = (url) => {
    if (!url) return "Desconocido";

    const extension = url.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return "PDF";
      case "docx":
      case "doc":
        return "Word";
      case "xlsx":
      case "xls":
        return "Excel";
      default:
        return extension.toUpperCase();
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Gestión de Documentos</h1>
        <Button
          onClick={handleShowModal}
          variant="success"
          className="d-flex align-items-center"
        >
          <i className="bi bi-plus-circle me-2"></i>
          <span className="d-none d-sm-inline">Subir Documento</span>
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error.message ||
            (typeof error === "string" ? error : "Ocurrió un error")}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="d-block d-md-none alert alert-info mb-3">
            <i className="bi bi-phone me-1"></i>
            Desliza horizontalmente para ver todas las columnas
          </div>

          <div className="table-responsive">
            <Table striped bordered hover className="small">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Título</th>
                  <th className="d-none d-md-table-cell">Descripción</th>
                  <th>Tipo</th>
                  <th className="text-center">Estado</th>
                  <th className="d-none d-sm-table-cell">Fecha de Subida</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {documentos.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      No hay documentos registrados
                    </td>
                  </tr>
                ) : (
                  documentos.map((doc) => (
                    <tr key={doc.id}>
                      <td>{doc.id}</td>
                      <td
                        title={doc.titulo}
                        className="text-truncate"
                        style={{ maxWidth: "150px" }}
                      >
                        {doc.titulo}
                      </td>
                      <td
                        title={doc.descripcion}
                        className="d-none d-md-table-cell text-truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        {doc.descripcion || "Sin descripción"}
                      </td>
                      <td>{getDocumentType(doc.url)}</td>
                      {/* celda estado — va después de <td>{getDocumentType(doc.url)}</td> */}
                      <td className="text-center">
                        <span
                          className={`badge ${doc.estado_activo ? "bg-success" : "bg-secondary"}`}
                        >
                          {doc.estado_activo ? "Visible" : "Oculto"}
                        </span>
                      </td>
                      <td>
                        {doc.descripcion?.trim() ? (
                          doc.descripcion
                        ) : (
                          <span className="text-muted">Sin descripción</span>
                        )}
                      </td>
                      <td className="d-none d-sm-table-cell">
                        {new Date(doc.fecha_subida).toLocaleDateString("es-CL")}
                      </td>
                      <td className="text-center">
                        <ButtonGroup size="sm">
                          <Button
                            variant="outline-primary"
                            href={doc.url}
                            target="_blank"
                            title="Ver"
                          >
                            <i className="bi bi-eye"></i>
                            <span className="d-none d-lg-inline ms-1">Ver</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => handleDeleteDocument(doc.id)}
                            title="Eliminar"
                          >
                            <i className="bi bi-trash"></i>
                            <span className="d-none d-lg-inline ms-1">
                              Eliminar
                            </span>
                          </Button>
                          {/* dentro del <ButtonGroup>, junto a Ver y Eliminar */}
                          <Button
                            variant={
                              doc.estado_activo
                                ? "outline-secondary"
                                : "outline-success"
                            }
                            onClick={() => handleToggleEstado(doc)}
                            title={doc.estado_activo ? "Ocultar" : "Mostrar"}
                          >
                            <i
                              className={`bi ${doc.estado_activo ? "bi-eye-slash" : "bi-eye"}`}
                            ></i>
                          </Button>
                          <Button
                            variant="outline-warning"
                            onClick={() => handleReemplazarArchivo(doc)}
                            title="Reemplazar archivo"
                          >
                            <i className="bi bi-arrow-repeat"></i>
                          </Button>
                          <Button
                            variant="outline-primary"
                            onClick={() => abrirEditar(doc)}
                            title="Editar título/descripción"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-upload me-2"></i>
            Subir Documento
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(localUploadError || serverUploadError) && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setLocalUploadError(null)}
            >
              {localUploadError || serverUploadError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3" controlId="nombre">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                type="text"
                value={documentDetails.nombre}
                onChange={handleNombreChange}
                isInvalid={!!validationErrors.nombre}
                placeholder="Ingrese el nombre del documento"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.nombre}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="descripcion">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={documentDetails.descripcion}
                onChange={handleDescripcionChange}
                isInvalid={!!validationErrors.descripcion}
                placeholder="Ingrese una descripción (opcional)"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.descripcion}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Máximo 200 caracteres
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3" controlId="archivo">
              <Form.Label>Archivo *</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,.docm,.dotx,.dot,.odt,.xlsx,.xls,.xlsm,.xltx,.xlsb,.ods"
                onChange={handleArchivoChange}
                isInvalid={!!validationErrors.archivo}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.archivo}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Formatos aceptados: PDF, Word, Excel. Tamaño máximo: 10MB.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadDocument}
            disabled={!isFormValid() || isUploading}
          >
            {isUploading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Subiendo...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-1"></i>
                Subir Documento
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!editando} onHide={() => setEditando(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar documento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Título *</Form.Label>
            <Form.Control
              value={formEdit.titulo}
              onChange={(e) =>
                setFormEdit((f) => ({ ...f, titulo: e.target.value }))
              }
              placeholder="Título del documento"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formEdit.descripcion}
              onChange={(e) =>
                setFormEdit((f) => ({ ...f, descripcion: e.target.value }))
              }
              placeholder="Descripción (opcional)"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditando(null)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={guardarEdicion}>
            Guardar cambios
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentosAdminPage;
